import { ColumnDef } from "@tanstack/table-core/build/lib/types";
import { ShipmentProduct, ProductComponent, StpDetails } from "../../graphql/graphql";
import { Link, Text } from "@nike/eds";

export type ShipmentProductsTableColumn<T> = ColumnDef<T, any> & {
  header: string | React.ReactNode | (() => React.ReactNode);
  accessorKey: keyof T | string;
};

export const getShipmentProductsTableColumns = (
  handleHtsCodeView: (args: { components: ProductComponent[] }) => void,
  handleShowInvoicesOrdersView: (args: { product: ShipmentProduct | null }) => void,
  handleStpDetailsView: (args: { stpDetails: StpDetails[] | [] }) => void,
): ShipmentProductsTableColumn<ShipmentProduct>[] => [
  {
    header: () => <span title="CR PO Number">CR PO Number</span>,
    accessorKey: "crpoNbr",
    size: 140,
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      const value = row.original.crpoNbr ?? "N/A";
      return (
        <Text as="span" className={value === "N/A" ? "eds-color--text-danger" : undefined}>
          {value}
        </Text>
      );
    },
  },
  {
    header: () => <span title="Line Item Number">Line Item Nbr</span>,
    accessorKey: "crPoItemVariantNbr",
    size: 140,
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      const value = row.original.crPoItemVariantNbr ?? "N/A";
      return (
        <Text as="span" className={value === "N/A" ? "eds-color--text-danger" : undefined}>
          {value}
        </Text>
      );
    },
  },
  {
    header: () => <span title="Product Code">Product Code</span>,
    accessorKey: "productCode",
    size: 150,
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    header: () => <span title="Size">Size</span>,
    accessorKey: "size",
    size: 120,
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      const value = row.original.size ?? "N/A";
      return (
        <Text as="span" className={value === "N/A" ? "eds-color--text-danger" : undefined}>
          {value}
        </Text>
      );
    },
  },
  {
    header: () => <span title="Division">Division</span>,
    accessorKey: "division",
    size: 120,
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    header: () => <span title="Quantity">Quantity</span>,
    accessorKey: "quantity",
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      const value = row.original.quantity ?? "N/A";
      return (
        <Text as="span" className={typeof value === "string" && value === "N/A" ? "eds-color--text-danger" : undefined}>
          {value}
        </Text>
      );
    },
  },
  {
    header: () => <span title="Net Weight (g)">Net Wt (g)</span>,
    accessorKey: "netWeightInGrams",
    size: 120,
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      const value = row.original.netWeightInGrams ?? "N/A";
      return (
        <Text as="span" className={value === "N/A" ? "eds-color--text-danger" : undefined}>
          {value}
        </Text>
      );
    },
  },
  {
    header: () => <span title="HTS Code">HTS Code</span>,
    accessorKey: "components",
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      const primaryComponent = (row.original.components || []).find(
        (c) => c?.primaryFlag === true
      );
      const value = primaryComponent?.classification?.code ?? "N/A";
      return (
        <Link
          disabled={value === "N/A"}
          onClick={() =>
            handleHtsCodeView({
              components: (row.original.components || []).filter(
                (c): c is ProductComponent => c != null
              ),
            })
          }
          className={value === "N/A" ? "eds-color--text-danger" : "eds-text-color--link"}
        >
          {value}
        </Link>
      );
    },
  },
  {
    header: () => <span title="Declaration Price">Declaration Price</span>,
    accessorKey: "declarationPrice",
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      const baseCommercialDocument = row.original.commercialDocuments?.find(
        (doc) => doc?.useAsBaseValue === true
      );
      const formatted =
        baseCommercialDocument?.currency != null &&
        baseCommercialDocument?.declarationAmount != null
          ? new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: baseCommercialDocument.currency,
            }).format(baseCommercialDocument.declarationAmount)
          : undefined;
      const value = formatted ? formatted : "N/A";
      return (
        <Link
          disabled={value === "N/A"}
          onClick={() =>
            handleShowInvoicesOrdersView({ product: row.original })
          }
          className={value === "N/A" ? "eds-color--text-danger" : "eds-text-color--link"}
        >
          {value}
        </Link>
      );
    },
  },
  {
    header: () => <span title="Is Sample?">Sample</span>,
    accessorKey: "isSample",
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      const value = row.original.isSample ? "Y" : "N";
      return <Text as="span">{value}</Text>;
    },
  },
  {
    header: () => <span title="STP Eligible">STP Eligible</span>,
    accessorKey: "stpDetails",
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      const value = row.original.stpDetails?.length ? "YES" : "NO";
      return (
        <Link
          disabled={!row.original.stpDetails?.length}
          onClick={() =>
            handleStpDetailsView({ stpDetails: (row.original.stpDetails || []).filter((d): d is StpDetails => d != null) })
          }
          className={!row.original.stpDetails?.length ? "eds-color--text-default" : "eds-text-color--link"}
        >
          {value}
        </Link>
      );
    },
  },
  {
    header: () => <span title="Product Quality">Product Qual</span>,
    accessorKey: "quality",
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      const value = row.original.quality ?? "N/A";
      return (
        <Text as="span" className={value === "N/A" ? "eds-color--text-danger" : undefined}>
          {value}
        </Text>
      );
    },
  },
  {
    header: () => <span title="Manufacturer Country">Mfg Country</span>,
    accessorKey: "manufacturerCountry",
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      const value = row.original.manufacturerCountry ?? "N/A";
      return (
        <Text as="span" className={value === "N/A" ? "eds-color--text-danger" : undefined}>
          {value}
        </Text>
      );
    },
  }
];